/**
 * What's new — the in-app product updates feed (Alex, 4 Nov 2026; backend
 * routes/announcements.js + services/whatsNewSync.js, migration 238).
 *
 * A megaphone in the sidebar with a blue count of unread updates; clicking it
 * slides this panel in from the right: newest first, each with its date, a
 * New / Improved label, the title, a short description, a picture or a short
 * clip (or a Storylane demo) and a "Try it" button that goes to the feature.
 * Opening the panel marks everything as read. Learn → What's New keeps the
 * full history.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface Update {
  id: number;
  title: string;
  body: string | null;
  storylane_url: string | null;
  created_at: string;
  image_url?: string | null;
  video_url?: string | null;
  cta_label?: string | null;
  cta_url?: string | null;
  category?: string | null;
  seen: boolean;
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

/** The unread count for the megaphone; refreshed on load and every 10 minutes. */
export function useWhatsNew(token: string | null | undefined) {
  const [updates, setUpdates] = useState<Update[] | null>(null);
  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch('/api/announcements/all-visible', { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setUpdates((await r.json()).announcements || []);
    } catch { /* quiet: the megaphone just shows no count */ }
  }, [token]);
  useEffect(() => {
    load();
    const t = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);
  const markAllRead = useCallback(async () => {
    setUpdates((u) => (u || []).map((x) => ({ ...x, seen: true })));
    try { await fetch('/api/announcements/read-all', { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); } catch { /* next load fixes it */ }
  }, [token]);
  const unread = (updates || []).filter((u) => !u.seen).length;
  return { updates, unread, markAllRead, reload: load };
}

/** The body text: blank lines = paragraphs, "- " lines = bullets. */
export const UpdateBody: React.FC<{ text: string }> = ({ text }) => (
  <div className="space-y-2">
    {text.split(/\n\s*\n/).map((block, i) => {
      const lines = block.split('\n').filter((l) => l.trim());
      if (lines.length && lines.every((l) => /^\s*[-•]\s+/.test(l))) {
        return (
          <ul key={i} className="space-y-1">
            {lines.map((l, j) => (
              <li key={j} className="flex gap-2"><span className="text-blue-500 mt-[1px]" aria-hidden>•</span><span>{l.replace(/^\s*[-•]\s+/, '')}</span></li>
            ))}
          </ul>
        );
      }
      return <p key={i}>{block.trim()}</p>;
    })}
  </div>
);

export const UpdateMedia: React.FC<{ u: Update }> = ({ u }) => {
  if (u.video_url) {
    return (
      <video src={u.video_url} poster={u.image_url || undefined} autoPlay muted loop playsInline
        className="w-full rounded-xl border border-gray-200 bg-gray-50" />
    );
  }
  if (u.image_url) {
    return <img src={u.image_url} alt="" loading="lazy" className="w-full rounded-xl border border-gray-200 bg-gray-50" />;
  }
  if (u.storylane_url && /^https?:\/\//.test(u.storylane_url.trim())) {
    return (
      <a href={u.storylane_url.trim()} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-blue-700 hover:bg-gray-100">
        ▶ Watch the walkthrough
      </a>
    );
  }
  return null;
};

export const CategoryLabel: React.FC<{ category?: string | null }> = ({ category }) => (
  category === 'improved'
    ? <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-50 text-violet-700">Improved</span>
    : <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700">New</span>
);

const WhatsNewPanel: React.FC<{
  open: boolean;
  onClose: () => void;
  updates: Update[] | null;
}> = ({ open, onClose, updates }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const list = updates || [];

  return (
    <div className="fixed inset-0 z-[85]" role="dialog" aria-modal="true" aria-labelledby="whats-new-title">
      <div className="absolute inset-0 bg-gray-900/30 backdrop-blur-[2px]" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
        <header className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <h2 id="whats-new-title" className="text-xl font-semibold text-gray-900 tracking-tight">What’s new</h2>
            <p className="text-sm text-gray-500 mt-0.5">The latest from MarketMapr</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none -mt-1" aria-label="Close">×</button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {updates === null ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
          ) : list.length === 0 ? (
            <p className="p-8 text-sm text-gray-500 text-center">Nothing new just yet.</p>
          ) : (
            <ol className="divide-y divide-gray-100">
              {list.map((u) => (
                <li key={u.id} className="px-6 py-6">
                  <div className="flex items-center gap-2">
                    <CategoryLabel category={u.category} />
                    <span className="text-xs text-gray-400">{fmtDate(u.created_at)}</span>
                    {!u.seen && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" aria-label="Unread" />}
                  </div>
                  <h3 className="mt-2 text-base font-semibold text-gray-900 leading-snug">{u.title}</h3>
                  {u.body && <div className="mt-2 text-sm text-gray-600 leading-relaxed"><UpdateBody text={u.body} /></div>}
                  <div className="mt-3"><UpdateMedia u={u} /></div>
                  {u.cta_url && (
                    <button
                      onClick={() => {
                        onClose();
                        if (/^https?:\/\//.test(u.cta_url!)) window.open(u.cta_url!, '_blank', 'noopener');
                        else navigate(u.cta_url!);
                      }}
                      className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800">
                      {u.cta_label || 'Try it'} <span aria-hidden>→</span>
                    </button>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
        <footer className="px-6 py-3 border-t border-gray-100 text-xs text-gray-500">
          <button onClick={() => { onClose(); navigate('/training?tab=whats-new'); }} className="hover:text-gray-800 underline">See every update in Learn</button>
        </footer>
      </aside>
    </div>
  );
};

export default WhatsNewPanel;
